import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Box, Typography, Checkbox, Button, Alert, Snackbar,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import InitiateRequestDialog from '../../components/books/InitiateRequestDialog';
import { getBooksApi, getBookFilterOptionsApi, getPublisherFilterOptionsApi, saveBooksApi } from '../../api/books';
import { TARGET_SEGMENTS } from '../../utils/constants';
import { hasActiveQuery, buildFilterParams } from '../../utils/bookFilterHelpers';

export default function UserBooks() {
  const navigate = useNavigate(); // kept for the row-click navigation below, currently disabled
  const [books, setBooks] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ message: '', severity: 'success' });

  const queryActive = hasActiveQuery(search, filters);

  const fetchBooks = useCallback(async () => {
    if (!hasActiveQuery(search, filters)) {
      setBooks([]);
      setTotal(0);
      setSelected([]);
      return;
    }
    const params = { page: page + 1, page_size: rowsPerPage, search, ...buildFilterParams(filters) };
    const res = await getBooksApi(params);
    setBooks(res.data.results || res.data);
    setTotal(res.data.count || res.data.length);
    setSelected([]);
  }, [page, rowsPerPage, search, filters]);

  useEffect(() => { fetchBooks(); }, [fetchBooks]);
  useEffect(() => {
    getPublisherFilterOptionsApi().then((r) => setPublishers(r.data || [])).catch(() => {});
    getBookFilterOptionsApi().then((r) => {
      setDisciplines(r.data.disciplines || []);
      setLanguages(r.data.languages || []);
    }).catch(() => {});
  }, []);

  const bookFilters = [
    { key: 'publisher', label: 'Publisher', type: 'select', multiple: true, options: publishers.map(p => ({ value: p.id, label: p.label })) },
    { key: 'discipline', label: 'Discipline', type: 'select', multiple: true, options: disciplines },
    { key: 'language', label: 'Language', type: 'select', multiple: true, options: languages },
    { key: 'target_segment', label: 'Target Segment', type: 'select', multiple: true, options: TARGET_SEGMENTS },
    { key: 'author_name', label: 'Author Name', type: 'text' },
  ];

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selected.length === books.length) setSelected([]);
    else setSelected(books.map((b) => b.id));
  };

  const openRequestDialog = () => {
    setRequestDialogOpen(true);
  };

  const handleRequestSuccess = (message) => {
    setRequestDialogOpen(false);
    setSelected([]);
    setSnackbar({ message, severity: 'success' });
  };

  const handleSaveForLater = async () => {
    setSaving(true);
    try {
      const res = await saveBooksApi(selected);
      setSelected([]);
      setSnackbar({ message: res.data.message || 'Book(s) saved for later.', severity: 'success' });
    } catch (err) {
      const data = err.response?.data;
      setSnackbar({ message: (data && data.error) || 'Failed to save books for later.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="Books">
      <PageHeader
        title="Browse Books"
        breadcrumbs={[{ label: 'Books' }]}
      />
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <SearchFilterBar
                searchPlaceholder="Search ISBN, title, author..."
                filters={bookFilters}
                onSearch={setSearch}
                onFilter={setFilters}
              />
            </Box>
            {selected.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<BookmarkAddIcon />}
                  onClick={handleSaveForLater}
                  disabled={saving}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {saving ? 'Saving...' : `Save for Later (${selected.length})`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={openRequestDialog}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Initiate Request ({selected.length})
                </Button>
              </Box>
            )}
          </Box>
          {!queryActive ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FilterAltIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Select a filter above (Publisher, Discipline, Language, Target Segment) or search to see matching books.
              </Typography>
            </Box>
          ) : (
          <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < books.length}
                    checked={books.length > 0 && selected.length === books.length}
                    onChange={toggleSelectAll}
                  />
                </TableCell>
                <TableCell>ISBN</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Publisher</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {books.map((b) => (
                <TableRow
                  key={b.id}
                  hover
                  selected={selected.includes(b.id)}
                  // Row click -> book detail page, disabled for now.
                  // To re-enable, uncomment the two lines below:
                  // sx={{ cursor: 'pointer' }}
                  // onClick={() => navigate(`/books/${b.id}`)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selected.includes(b.id)} onChange={() => toggleSelect(b.id)} />
                  </TableCell>
                  <TableCell>{b.isbn}</TableCell>
                  <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</TableCell>
                  <TableCell>{b.author_name}</TableCell>
                  <TableCell>{b.publisher_company || b.publisher_name}</TableCell>
                  <TableCell>{b.language}</TableCell>
                  <TableCell>₹{b.price}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {books.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">No books found</Typography>
            </Box>
          )}

          <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100, 500, 1000]} />
          </>
          )}
        </CardContent>
      </Card>

      <InitiateRequestDialog
        open={requestDialogOpen}
        onClose={() => setRequestDialogOpen(false)}
        selectedIds={selected}
        onSuccess={handleRequestSuccess}
      />

      <Snackbar open={!!snackbar.message} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, message: '' })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, message: '' })}>{snackbar.message}</Alert>
      </Snackbar>
    </Layout>
  );
}
